import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import Api, {DEFAULT_ERROR_MESSAGE} from '../../../../api/Api'
import { GLOBAL_HD_DTU_ID } from '../../../../utils/common'
import { trackUpdate, EVENT_ANALYTICS_TYPE_UPDATED } from '../../../../utils/matomoEvents'
import {BASIC_ANALYTICS, RosePlotChart} from "../../../../types/analytics";

interface RosePlotLogicProps {
  coordinates: number[]
  timeStepInterval: string
  pointLabel: string
  isFwe?: boolean
  fwePointName?: string
}

export const useRosePlot = ({ coordinates, timeStepInterval, pointLabel, isFwe, fwePointName }: RosePlotLogicProps) => {
  const [updateRosePlotData, setUpdatedRosePlotData] = useState<RosePlotChart | null>(null)
  const [errorRosePlot, setErrorRosePlot] = useState('')
  const pointName = isFwe ? fwePointName ?? pointLabel : pointLabel

  const rosePlotMutation = useMutation({
    mutationFn: async (values: any) => {
      const data = await Api.chart.getRosePlotV2(
        crypto.randomUUID(),
        pointName,
        values.datasetName,
        values.startDate,
        values.endDate,
        coordinates.join(),
        values.datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
        values.legendInterval,
        values.valueColumnName,
        values.directionColumnName,
        values.degreeInterval,
      )
      return { data, datasetName: values.datasetName }
    },
    onSuccess: ({ data, datasetName }) => {
      if (data) {
        setUpdatedRosePlotData(data)
        trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, coordinates, datasetName, BASIC_ANALYTICS.ROSE_PLOT.toLowerCase())
        setErrorRosePlot('')
      } else {
        setErrorRosePlot(DEFAULT_ERROR_MESSAGE)
      }
    },
    onError: (error: any) => {
      setErrorRosePlot(error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE)
      setUpdatedRosePlotData(null)
    }
  })

  const onUpdateRosePlot = useCallback(async (values: any) => {
    rosePlotMutation.mutate(values)
  }, [rosePlotMutation])

  const resetRosePlot = () => {
    setUpdatedRosePlotData(null)
    setErrorRosePlot('')
  }

  return {
    updateRosePlotData,
    setUpdatedRosePlotData,
    errorRosePlot,
    resetRosePlot,
    onUpdateRosePlot,
    loadingRosePlot: rosePlotMutation.isPending
  }
}
